/**
 * @fileOverview This file contains the implementation of the warehouse admon datagrid for Inventory module
 * @version 1.0.0
 * @since 1.0.0
 * @author Diego Benitez Reyna
 */

//#region imports
import React, { useEffect, useState } from 'react';
import customAxios from '../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { GlobalVariables } from '../../globalVar';

//MUI imports
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { DeleteOutlineOutlined as DeleteIcon } from '@mui/icons-material';

//Translation imports
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import { useTranslation } from 'react-i18next';

//Modals
import ModalAddInventoryType from '../Modal/Create/ModalAddInventoryType';
import ModalUInventoryType from '../Modal/Update/ModalUInventoryType';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd } from '@fortawesome/free-solid-svg-icons';
import i18next from 'i18next';
import ModalDInventoryType from '../Modal/Delete/ModalDInventoryType';
import { useTableLocale } from '../../hooks/useTableLocale';

const AdmonWarehouseTable = () => {
  const { t } = useTranslation();
  const locale = useTableLocale();

  const [inventoryTypeData, setInventoryTypeData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalData, setEditModalData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState({});

  const {
    nameString,
    descriptionString,
    statusString,
    createdByString,
    updatedByString,
    createdAtString,
    updatedAtString,
    addString,
    editString,
    deleteString,
    detailString,
    inventoryWarehouseAdmonStr,
  } = useTranslationHook();

  const getInventoryTypeData = async () => {
    try {
      const inventoryTypeRes = await customAxios.get(
        `${GlobalVariables.getURL()}inventory/getInventoryTypesList`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        },
      );

      const inventoryData = inventoryTypeRes.data.data
        ? inventoryTypeRes.data.data.map((inventory) => {
            return {
              ...inventory,
              is_active: t(`${inventory.is_active}`),
            };
          })
        : [];

      setInventoryTypeData(inventoryData);
    } catch (error) {
      console.log(error);
      setInventoryTypeData([]);
    }
  };

  useEffect(() => {
    const updateInventoryTypeData = () => {
      setInventoryTypeData((prevData) => {
        return prevData.map((inventory) => ({
          ...inventory,
          is_active: t(`${inventory.is_active}`),
        }));
      });
    };
    i18next.on('languageChanged', updateInventoryTypeData);

    return () => {
      i18next.off('languageChanged', updateInventoryTypeData);
    };
  }, [t]);

  useEffect(() => {
    getInventoryTypeData();
  }, []);

  // Columns
  const inventoryTypesColumns = [
    {
      field: 'name',
      headerName: nameString,
      width: 200,
    },
    {
      field: 'description',
      headerName: descriptionString,
      width: 300,
    },
    {
      field: 'is_active',
      headerName: statusString,
      width: 150,
      renderCell: (data) => {
        if (
          data.row.is_active === 'Activo' ||
          data.row.is_active === 'Active'
        ) {
          return (
            <div>
              <span className="statusActive">{data.row.is_active}</span>
            </div>
          );
        } else {
          return (
            <div>
              <span className="statusInactive">{data.row.is_active}</span>
            </div>
          );
        }
      },
    },
    {
      field: 'created_by',
      headerName: createdByString,
      width: 150,
    },
    {
      field: 'updated_by',
      headerName: updatedByString,
      width: 150,
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: 150,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: 150,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  //Action column
  const actionColumn = [
    {
      field: 'action',
      headerName: detailString,
      headerClassName: 'equip-datagrid-actions-tour',
      width: 115,
      renderCell: (params) => {
        return (
          <div className="cellAction equip-datagrid-actions-tour">
            <Tooltip title={editString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={() => {
                  setEditModalData(params.row);
                  setShowEditModal(true);
                }}
              >
                <ModeEditOutlineOutlinedIcon
                  className="detail-icons"
                  id="update-icon"
                />
              </button>
            </Tooltip>
            <Tooltip title={deleteString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={() => {
                  setDeleteModalData(params.row);
                  setShowDeleteModal(true);
                }}
              >
                <DeleteIcon className="detail-icons" id="delete-icon" />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">{inventoryWarehouseAdmonStr}</span>
          <button
            className="add-button create-equip-tour"
            onClick={() => setShowAddModal(true)}
          >
            {addString} <FontAwesomeIcon icon={faAdd} />
          </button>
        </div>
      </div>

      {/* MODALS */}
      {showAddModal && (
        <ModalAddInventoryType
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          refreshData={async () => await getInventoryTypeData()}
        />
      )}

      {showEditModal && (
        <ModalUInventoryType
          data={editModalData}
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            setEditModalData({});
          }}
          refreshData={async () => await getInventoryTypeData()}
        />
      )}

      {showDeleteModal && (
        <ModalDInventoryType
          data={deleteModalData}
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          refreshData={async () => await getInventoryTypeData()}
        />
      )}
      {/* END MODALS */}

      <DataGridPro
        pagination
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
          pinnedColumns: { right: ['action'] },
        }}
        rows={inventoryTypeData}
        columns={inventoryTypesColumns.concat(actionColumn)}
        disableSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
      />
    </>
  );
};

export default AdmonWarehouseTable;
